import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DeleteOutlineRounded as DeleteOutlineIcon,
  ExploreRounded as ExploreIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import {
  api,
  Sitemap3dBlockInput,
  Sitemap3dCandidate,
  Sitemap3dDetail,
  Sitemap3dProject,
  sitemap3dSitePlanToSitePlanData,
} from '../../services/api';
import NorthCalibrationDialog from '../../components/solar/NorthCalibrationDialog';

/**
 * Footprint of a plotted item, as a % of the site plan — the same fixed 8×8 the
 * Block Identification Module drops, so plotting feels identical across the two.
 */
const PLOT_SIZE = 8;
const MAX_SITE_PLAN_BYTES = 8 * 1024 * 1024;

/** Same eight options the Block Identification Module offers. */
const FACINGS = ['North', 'NorthEast', 'East', 'SouthEast', 'South', 'SouthWest', 'West', 'NorthWest'] as const;

/** One plotted item: a block marker, or a stack (which the 3D massing extrudes). */
interface PlottedItem {
  blockNumber?: string;
  stack?: string;
  facing?: string;
  /** Storey count. Stacks only — it is the massing height. */
  floors?: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Identity of a plotted item, and the Map key it is stored under. Deliberately
 * block + stack only, with no facing: that is exactly the key the save endpoint
 * upserts on, so re-plotting the same stack with a different facing updates the
 * row instead of creating a second one.
 */
function coordKey(item: { blockNumber?: string; stack?: string }): string {
  const block = (item.blockNumber || '').trim();
  const stack = (item.stack || '').trim();
  if (block && !stack) return `block:${block}`;
  if (block && stack) return `block:${block}:stack:${stack}`;
  if (stack) return `stack:${stack}`;
  return '';
}

function itemLabel(item: PlottedItem): string {
  const isBlock = Boolean(item.blockNumber) && !item.stack;
  if (isBlock) return item.blockNumber!;
  const base = item.blockNumber ? `${item.blockNumber} - ${item.stack}` : `${item.stack}`;
  const facing = item.facing ? ` (${item.facing})` : '';
  const floors = item.floors ? ` · ${item.floors}F` : '';
  return `${base}${facing}${floors}`;
}

interface Manage3dSitemapDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a change that the agent-facing list should pick up. */
  onProjectsChanged?: () => void | Promise<void>;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Manage 3D Sitemap (admin): switch non-new-launch SG residential projects on,
 * give each one a site plan, plot its blocks and stacks, and calibrate north so
 * the 3D and 3DS views have something to work with.
 *
 * The plotting surface mirrors the Block Identification Module — click the plan
 * to open a Block / Stack popover, fixed-size boxes, drag to move, delete icon
 * on the box, "Show Plotted Points" toggle — and writes the same
 * ProjectSitePlanCoordinates rows. Two deliberate differences, both forced by
 * the data: these projects have no unit inventory, so the block and stack
 * pickers are free text rather than dropdowns fed by ProjectUnit, and a stack
 * carries a storey count because there are no units to count floors from.
 */
const Manage3dSitemapDialog: React.FC<Manage3dSitemapDialogProps> = ({ open, onClose, onProjectsChanged }) => {
  const [projects, setProjects] = useState<Sitemap3dProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [candidates, setCandidates] = useState<Sitemap3dCandidate[]>([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateToAdd, setCandidateToAdd] = useState<Sitemap3dCandidate | null>(null);
  const [adding, setAdding] = useState(false);

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [detail, setDetail] = useState<Sitemap3dDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [coordinates, setCoordinates] = useState<Map<string, PlottedItem>>(new Map());
  const [showPlottedPoints, setShowPlottedPoints] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Plotting popover
  const [popoverAnchor, setPopoverAnchor] = useState<{ x: number; y: number } | null>(null);
  const [clickedPoint, setClickedPoint] = useState<{ x: number; y: number } | null>(null);
  const [coordinateType, setCoordinateType] = useState<'block' | 'stack'>('stack');
  const [blockInput, setBlockInput] = useState('');
  const [stackInput, setStackInput] = useState('');
  const [facingInput, setFacingInput] = useState('');
  const [floorsInput, setFloorsInput] = useState('');
  const [blockForStack, setBlockForStack] = useState('');

  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);

  const [northOpen, setNorthOpen] = useState(false);
  const [draftNorth, setDraftNorth] = useState(0);
  const [northSaving, setNorthSaving] = useState(false);
  const [northError, setNorthError] = useState('');
  const [northAngle, setNorthAngle] = useState<number | null>(null);

  const sitePlanContainerRef = useRef<HTMLDivElement | null>(null);

  const selectedProject = useMemo(
    () => projects.find((project) => project.projectId === selectedProjectId) || null,
    [projects, selectedProjectId],
  );

  /** Block numbers already plotted, offered as shortcuts in the popover. */
  const knownBlockNumbers = useMemo(() => {
    const set = new Set<string>();
    coordinates.forEach((item) => { if (item.blockNumber) set.add(item.blockNumber); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [coordinates]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProjects(await api.getSitemap3dProjects());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the 3D Sitemap');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (projectId: string) => {
    setDetailLoading(true);
    setError('');
    try {
      const result = await api.getSitemap3dProject(projectId);
      setDetail(result);
      setNorthAngle(result.northAngle);
      setDraftNorth(result.northAngle ?? 0);
      const next = new Map<string, PlottedItem>();
      (result.sitePlan?.blocks || []).forEach((block) => {
        const item: PlottedItem = {
          blockNumber: block.blockNumber || undefined,
          stack: block.stack || undefined,
          facing: block.facing || undefined,
          floors: block.floors != null ? String(block.floors) : undefined,
          x: Number(block.x),
          y: Number(block.y),
          width: Number(block.width),
          height: Number(block.height),
        };
        const key = coordKey(item);
        if (key) next.set(key, item);
      });
      setCoordinates(next);
      setDirty(false);
    } catch (err) {
      setDetail(null);
      setCoordinates(new Map());
      setError(err instanceof Error ? err.message : 'Failed to load the site plan');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadProjects();
  }, [open, loadProjects]);

  useEffect(() => {
    if (open) return;
    setSelectedProjectId('');
    setDetail(null);
    setCoordinates(new Map());
    setDirty(false);
    setNotice('');
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCandidatesLoading(true);
    const timer = window.setTimeout(() => {
      api.getSitemap3dCandidates(candidateSearch)
        .then((rows) => { if (!cancelled) setCandidates(rows); })
        .catch(() => { if (!cancelled) setCandidates([]); })
        .finally(() => { if (!cancelled) setCandidatesLoading(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [open, candidateSearch]);

  const handleSelectProject = useCallback(async (projectId: string) => {
    if (dirty && !window.confirm('Discard the unsaved plotting changes?')) return;
    setSelectedProjectId(projectId);
    await loadDetail(projectId);
  }, [dirty, loadDetail]);

  const handleAddProject = useCallback(async () => {
    if (!candidateToAdd) return;
    setAdding(true);
    setError('');
    try {
      await api.addSitemap3dProject(candidateToAdd.projectId);
      setNotice(`${candidateToAdd.projectName} added as a draft. Upload a site plan and plot its stacks.`);
      const added = candidateToAdd.projectId;
      setCandidateToAdd(null);
      setCandidateSearch('');
      await loadProjects();
      await onProjectsChanged?.();
      setSelectedProjectId(added);
      await loadDetail(added);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the project');
    } finally {
      setAdding(false);
    }
  }, [candidateToAdd, loadDetail, loadProjects, onProjectsChanged]);

  const handleTogglePublished = useCallback(async (project: Sitemap3dProject, isPublished: boolean) => {
    setError('');
    try {
      await api.updateSitemap3dProject(project.projectId, { isPublished });
      await loadProjects();
      await onProjectsChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the project');
    }
  }, [loadProjects, onProjectsChanged]);

  const handleRemove = useCallback(async (project: Sitemap3dProject) => {
    if (!window.confirm(`Remove ${project.projectName} from the 3D Sitemap? The site plan and plotted stacks are kept.`)) return;
    setError('');
    try {
      await api.removeSitemap3dProject(project.projectId);
      if (selectedProjectId === project.projectId) {
        setSelectedProjectId('');
        setDetail(null);
        setCoordinates(new Map());
        setDirty(false);
      }
      await loadProjects();
      await onProjectsChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove the project');
    }
  }, [loadProjects, onProjectsChanged, selectedProjectId]);

  const handleUpload = useCallback(async (file: File | null) => {
    if (!file || !selectedProjectId) return;
    if (!file.type.startsWith('image/')) {
      setError('Only image uploads are supported for a site plan.');
      return;
    }
    if (file.size > MAX_SITE_PLAN_BYTES) {
      setError('Site plan image is too large. Maximum size is 8MB.');
      return;
    }
    if (coordinates.size > 0 && !window.confirm('Replacing the site plan clears every plotted item. Continue?')) return;

    setUploading(true);
    setError('');
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(new Error('Failed to read the image file'));
        reader.readAsDataURL(file);
      });
      await api.uploadSitemap3dSitePlan(selectedProjectId, dataUrl);
      setNotice('Site plan uploaded.');
      await loadDetail(selectedProjectId);
      await loadProjects();
      await onProjectsChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload the site plan');
    } finally {
      setUploading(false);
    }
  }, [coordinates.size, loadDetail, loadProjects, onProjectsChanged, selectedProjectId]);

  const closePopover = useCallback(() => {
    setPopoverAnchor(null);
    setClickedPoint(null);
    setBlockInput('');
    setStackInput('');
    setFacingInput('');
    setFloorsInput('');
    setBlockForStack('');
  }, []);

  /** Click an empty part of the plan to open the Block / Stack popover.
   *  Clicking an existing item is left to that item's own handlers. */
  const handleSitePlanClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (draggedKey || !detail?.sitePlan?.imageUrl) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    if (showPlottedPoints) {
      const onExisting = Array.from(coordinates.values()).some((item) => (
        x >= item.x && x <= item.x + item.width && y >= item.y && y <= item.y + item.height
      ));
      if (onExisting) return;
    }

    setPopoverAnchor({ x: event.clientX, y: event.clientY });
    setClickedPoint({ x, y });
  }, [coordinates, detail, draggedKey, showPlottedPoints]);

  /** Drop the item at the clicked point, centred on it like the Block module. */
  const plotItem = useCallback((item: Omit<PlottedItem, 'x' | 'y' | 'width' | 'height'>) => {
    if (!clickedPoint) return;
    const key = coordKey(item);
    if (!key) return;
    const existing = coordinates.get(key);
    setCoordinates((prev) => {
      const next = new Map(prev);
      next.set(key, {
        ...item,
        x: clamp(clickedPoint.x - PLOT_SIZE / 2, 0, 100 - PLOT_SIZE),
        y: clamp(clickedPoint.y - PLOT_SIZE / 2, 0, 100 - PLOT_SIZE),
        // Re-plotting an existing item keeps whatever size it was given.
        width: existing?.width ?? PLOT_SIZE,
        height: existing?.height ?? PLOT_SIZE,
      });
      return next;
    });
    setDirty(true);
    closePopover();
  }, [clickedPoint, closePopover, coordinates]);

  const handleDelete = useCallback((key: string) => {
    setCoordinates((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setDirty(true);
  }, []);

  const handleItemMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>, key: string) => {
    event.stopPropagation();
    const item = coordinates.get(key);
    const container = sitePlanContainerRef.current;
    if (!item || !container) return;
    const rect = container.getBoundingClientRect();
    setDraggedKey(key);
    setDragOffset({
      x: ((event.clientX - rect.left) / rect.width) * 100 - item.x,
      y: ((event.clientY - rect.top) / rect.height) * 100 - item.y,
    });
  }, [coordinates]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!draggedKey || !dragOffset || !sitePlanContainerRef.current) return;
    const rect = sitePlanContainerRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
    const y = ((event.clientY - rect.top) / rect.height) * 100 - dragOffset.y;
    setCoordinates((prev) => {
      const next = new Map(prev);
      const item = next.get(draggedKey);
      if (item) {
        next.set(draggedKey, {
          ...item,
          x: clamp(x, 0, 100 - item.width),
          y: clamp(y, 0, 100 - item.height),
        });
      }
      return next;
    });
    setDirty(true);
  }, [dragOffset, draggedKey]);

  const handleMouseUp = useCallback(() => {
    setDraggedKey(null);
    setDragOffset(null);
  }, []);

  useEffect(() => {
    if (!draggedKey) return undefined;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedKey, handleMouseMove, handleMouseUp]);

  const handleSaveCoordinates = useCallback(async () => {
    if (!selectedProjectId) return;
    setSaving(true);
    setError('');
    try {
      const payload: Sitemap3dBlockInput[] = Array.from(coordinates.values()).map((item) => ({
        blockNumber: item.blockNumber?.trim() || null,
        stack: item.stack?.trim() || null,
        facing: item.facing?.trim() || null,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        floors: item.floors?.trim() ? Number(item.floors) : null,
      }));
      await api.saveSitemap3dBlocks(selectedProjectId, payload);
      await loadDetail(selectedProjectId);
      setNotice(`Saved ${payload.length} coordinate${payload.length === 1 ? '' : 's'}.`);
      await loadProjects();
      await onProjectsChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the plotted coordinates');
    } finally {
      setSaving(false);
    }
  }, [coordinates, loadDetail, loadProjects, onProjectsChanged, selectedProjectId]);

  const handleSaveNorth = useCallback(async () => {
    const sitePlan = detail?.sitePlan;
    if (!sitePlan || !selectedProjectId) return;
    setNorthSaving(true);
    setNorthError('');
    try {
      const result = await api.sitePlanSolar(selectedProjectId, sitemap3dSitePlanToSitePlanData(sitePlan), ((draftNorth % 360) + 360) % 360);
      setNorthAngle(result.northAngle);
      setNorthOpen(false);
      setNotice('North saved for this site plan.');
      await loadProjects();
      await onProjectsChanged?.();
    } catch (err) {
      setNorthError(err instanceof Error ? err.message : 'Unable to save north');
    } finally {
      setNorthSaving(false);
    }
  }, [detail, draftNorth, loadProjects, onProjectsChanged, selectedProjectId]);

  const sitePlanImageUrl = detail?.sitePlan?.imageUrl || '';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage 3D Sitemap</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

          {/* The hint sits under the whole row, not inside the field: a TextField
              helperText grows the Autocomplete and pushes the button off the
              input's centre line. */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
              <Autocomplete
                sx={{ minWidth: 0, flexGrow: 1 }}
                options={candidates}
                loading={candidatesLoading}
                value={candidateToAdd}
                onChange={(_, value) => setCandidateToAdd(value)}
                inputValue={candidateSearch}
                onInputChange={(_, value, reason) => { if (reason !== 'reset') setCandidateSearch(value); }}
                getOptionLabel={(option) => option.projectName}
                isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
                renderOption={(props, option) => (
                  <li {...props} key={option.projectId}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.projectName}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                        {[option.district ? `D${String(option.district).replace(/^D/i, '')}` : null, option.region, option.topDate ? `TOP ${option.topDate}` : null]
                          .filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Add a project"
                    placeholder="Search SG residential projects outside Top Viewing Site Plans"
                  />
                )}
              />
              <Button
                variant="contained"
                startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                disabled={!candidateToAdd || adding}
                onClick={() => void handleAddProject()}
                // Matches the small TextField's 40px height so both tops and
                // bottoms line up.
                sx={{ height: 40, flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--color-text-muted)' }}>
              Only Singapore residential projects that the Top Viewing Site Plans tab does not already list.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
            {/* Enabled projects */}
            <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, maxHeight: '70vh', overflowY: 'auto', border: '1px solid rgba(var(--color-text-rgb), 0.12)', borderRadius: 1 }}>
              {loading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
              ) : projects.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, color: 'var(--color-text-muted)' }}>
                  No projects yet. Add one above.
                </Typography>
              ) : projects.map((project) => (
                <Box
                  key={project.projectId}
                  onClick={() => void handleSelectProject(project.projectId)}
                  sx={{
                    p: 1.25,
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                    bgcolor: project.projectId === selectedProjectId ? 'rgb(var(--color-accent-rgb) / 0.14)' : 'transparent',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{project.projectName}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip size="small" label={`${project.stackCount} plotted`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={project.northAngle != null ? 'success' : 'default'}
                      label={project.northAngle != null ? `N ${project.northAngle.toFixed(0)}°` : 'No north'}
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <FormControlLabel
                      onClick={(event) => event.stopPropagation()}
                      control={(
                        <Switch
                          size="small"
                          checked={project.isPublished}
                          onChange={(event) => void handleTogglePublished(project, event.target.checked)}
                        />
                      )}
                      label={<Typography variant="caption">{project.isPublished ? 'Published' : 'Draft'}</Typography>}
                    />
                    <Tooltip title="Remove from 3D Sitemap">
                      <IconButton
                        size="small"
                        onClick={(event) => { event.stopPropagation(); void handleRemove(project); }}
                        aria-label={`Remove ${project.projectName}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Plotting surface */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {!selectedProjectId ? (
                <Alert severity="info">Pick a project on the left to upload its site plan and plot the stacks.</Alert>
              ) : detailLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
                      {selectedProject?.projectName || selectedProjectId}
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : (sitePlanImageUrl ? 'Replace Site Plan' : 'Upload Site Plan')}
                      <input
                        hidden
                        accept="image/png,image/jpeg,image/webp"
                        type="file"
                        onChange={(event) => {
                          const file = event.target.files?.[0] ?? null;
                          event.currentTarget.value = '';
                          void handleUpload(file);
                        }}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ExploreIcon />}
                      disabled={!sitePlanImageUrl}
                      onClick={() => { setDraftNorth(northAngle ?? 0); setNorthError(''); setNorthOpen(true); }}
                    >
                      {northAngle != null ? `North ${northAngle.toFixed(1)}°` : 'Set North'}
                    </Button>
                  </Box>

                  {!sitePlanImageUrl ? (
                    <Alert severity="warning">Upload a site plan image before plotting.</Alert>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Click anywhere on the site plan to add a block or stack. Drag existing items to move them.
                        Click the delete icon to remove an item. A stack&apos;s storey count is what the 3D massing extrudes.
                      </Alert>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={showPlottedPoints}
                              onChange={(event) => setShowPlottedPoints(event.target.checked)}
                              color="primary"
                            />
                          )}
                          label={(
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Show Plotted Points ({coordinates.size})
                            </Typography>
                          )}
                        />
                      </Box>

                      <Box
                        ref={sitePlanContainerRef}
                        sx={{ position: 'relative', width: '100%' }}
                        onClick={handleSitePlanClick}
                      >
                        <Box
                          component="img"
                          src={sitePlanImageUrl}
                          alt="Site Plan"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            cursor: 'crosshair',
                            pointerEvents: 'none',
                            userSelect: 'none',
                          }}
                        />
                        {showPlottedPoints && Array.from(coordinates.entries()).map(([key, item]) => {
                          const isBlock = Boolean(item.blockNumber) && !item.stack;
                          return (
                            <Box
                              key={key}
                              onMouseDown={(event) => handleItemMouseDown(event, key)}
                              sx={{
                                position: 'absolute',
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                width: `${item.width}%`,
                                height: `${item.height}%`,
                                border: '2px solid',
                                borderColor: draggedKey === key ? 'error.main' : (isBlock ? 'primary.main' : 'secondary.main'),
                                bgcolor: draggedKey === key
                                  ? 'rgb(var(--color-danger-rgb) / 0.3)'
                                  : (isBlock ? 'rgb(var(--color-info-rgb) / 0.2)' : 'rgb(var(--color-success-rgb) / 0.2)'),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: draggedKey === key ? 'grabbing' : 'grab',
                                pointerEvents: 'auto',
                                '&:hover': {
                                  bgcolor: draggedKey === key
                                    ? 'rgb(var(--color-danger-rgb) / 0.4)'
                                    : (isBlock ? 'rgb(var(--color-info-rgb) / 0.3)' : 'rgb(var(--color-success-rgb) / 0.3)'),
                                },
                              }}
                            >
                              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    bgcolor: isBlock ? 'primary.main' : 'secondary.main',
                                    color: 'var(--color-text-strong)',
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    fontWeight: 'bold',
                                    pointerEvents: 'none',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {itemLabel(item)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(event) => { event.stopPropagation(); handleDelete(key); }}
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    bgcolor: 'error.main',
                                    color: 'var(--color-text-strong)',
                                    width: 20,
                                    height: 20,
                                    '&:hover': { bgcolor: 'error.dark' },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" sx={{ mr: 'auto', ml: 1, color: 'var(--color-text-muted)' }}>
            Publish a project once its stacks are plotted and north is set.
          </Typography>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            onClick={() => void handleSaveCoordinates()}
            disabled={!selectedProjectId || !sitePlanImageUrl || saving || !dirty}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save Coordinates ({coordinates.size})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block / Stack picker, anchored where the admin clicked — the same
          shape as the Block Identification Module's popover. The pickers are
          free text here because these projects have no unit inventory to
          enumerate blocks and stacks from. */}
      <Popover
        open={popoverAnchor !== null}
        onClose={closePopover}
        anchorReference="anchorPosition"
        anchorPosition={popoverAnchor ? { top: popoverAnchor.y, left: popoverAnchor.x } : undefined}
      >
        <Box sx={{ minWidth: 320 }}>
          <Tabs
            value={coordinateType}
            onChange={(_, value) => { setCoordinateType(value); if (value === 'block') setBlockForStack(''); }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Block" value="block" />
            <Tab label="Stack" value="stack" />
          </Tabs>

          {coordinateType === 'block' ? (
            <Box sx={{ p: 2, maxHeight: 420, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Block Number:</Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                label="Block number"
                value={blockInput}
                onChange={(event) => setBlockInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && blockInput.trim()) {
                    event.preventDefault();
                    plotItem({ blockNumber: blockInput.trim() });
                  }
                }}
                sx={{ mb: 1.5 }}
              />
              <Button
                fullWidth
                variant="contained"
                disabled={!blockInput.trim()}
                onClick={() => plotItem({ blockNumber: blockInput.trim() })}
              >
                {coordinates.has(`block:${blockInput.trim()}`) ? 'Update Block' : 'Add Block'}
              </Button>
              {knownBlockNumbers.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, color: 'var(--color-text-muted)' }}>
                    Already plotted
                  </Typography>
                  {knownBlockNumbers.map((blockNum) => (
                    <Button
                      key={blockNum}
                      fullWidth
                      variant="outlined"
                      onClick={() => plotItem({ blockNumber: blockNum })}
                      sx={{ mb: 0.5 }}
                    >
                      {blockNum} (Update)
                    </Button>
                  ))}
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Stack details:</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Block (optional)</InputLabel>
                <Select
                  value={blockForStack}
                  onChange={(event) => setBlockForStack(event.target.value)}
                  label="Block (optional)"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {knownBlockNumbers.map((blockNum) => (
                    <MenuItem key={blockNum} value={blockNum}>{blockNum}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                autoFocus
                label="Stack"
                value={stackInput}
                onChange={(event) => setStackInput(event.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Facing (optional)</InputLabel>
                <Select
                  value={facingInput}
                  onChange={(event) => setFacingInput(event.target.value)}
                  label="Facing (optional)"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {FACINGS.map((facing) => (
                    <MenuItem key={facing} value={facing}>{facing.replace(/([a-z])([A-Z])/g, '$1 $2')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Storeys"
                helperText="Drives the 3D massing height."
                value={floorsInput}
                onChange={(event) => setFloorsInput(event.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                disabled={!stackInput.trim()}
                onClick={() => plotItem({
                  blockNumber: blockForStack || undefined,
                  stack: stackInput.trim(),
                  facing: facingInput || undefined,
                  floors: floorsInput.trim() || undefined,
                })}
              >
                {coordinates.has(coordKey({ blockNumber: blockForStack, stack: stackInput.trim() })) ? 'Update Stack' : 'Add Stack'}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>

      {sitePlanImageUrl && (
        <NorthCalibrationDialog
          open={northOpen}
          imageUrl={sitePlanImageUrl}
          value={draftNorth}
          onChange={setDraftNorth}
          description="Rotate the arrow until it points the same way as N on this site plan. This is saved for this image and used at every viewing angle."
          saving={northSaving}
          error={northError}
          onCancel={() => setNorthOpen(false)}
          onConfirm={() => void handleSaveNorth()}
        />
      )}
    </>
  );
};

export default Manage3dSitemapDialog;
